import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ReactComponent as EyeIcon } from '../../../assets/icons/eye.svg';
import { ReactComponent as TrashIcon } from '../../../assets/icons/TrashBin2.svg';
import ConfirmModal from '../../BaseComponents/Modal/ConfirmModal';
import { getMemberFile } from '../../../api/members';
import Loader from '../../BaseComponents/Loader';
import './Files.scss';
import ViewFileModal from './ViewFileModal';

interface FilesTableProps {
    files: any[];
    onDelete: (id: number | string) => void;
    isLoading?: boolean;
    isFetchingMore?: boolean;
    memberId?: string;
    onLoadMore?: () => void;
    hasMore?: boolean;
}

const FilesTable: React.FC<FilesTableProps> = ({
    files,
    onDelete,
    isLoading = false,
    isFetchingMore = false,
    memberId,
    onLoadMore,
    hasMore = false
}) => {
    const [viewFile, setViewFile] = useState<any | null>(null);
    const [isFetchingFile, setIsFetchingFile] = useState(false);
    const [fileIdToDelete, setFileIdToDelete] = useState<number | string | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const formatFileType = (type: string) => {
        if (!type) return 'Other';
        
        const typeMap: Record<string, string> = {
            'imaging_scans': 'Imaging & Scans',
            'lab_results': 'Lab Results',
            'reports_letters': 'Reports & Letters',
            'medications': 'Medications',
            'visits_records': 'Visits & Records',
            'patient_uploads': 'Patient Uploads'
        };
        return typeMap[type] || (type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' '));
    };

    const handleAction = async (action: 'view' | 'delete', file: any) => {
        const id = file.id;
        if (action === 'view') {
            if (!memberId || !file?.id) {
               
                const fileData = {
                    ...file,
                    fileName: file.file_name || file.fileName,
                    url: file.view_url || file.url,
                    type: formatFileType(file.file_type || file.type)
                };
                setViewFile(fileData);
                return;
            }

            try {
                setIsFetchingFile(true);
                const response = await getMemberFile(file.id);
                const fileData = {
                    ...response,
                    fileName: response.file_name || file.file_name,
                    url: response.view_url || response.url || file.view_url,
                    type: formatFileType(response.file_type || file.file_type)
                };
                setViewFile(fileData);
            } catch (error) {
                console.error('Failed to fetch file details:', error);
               
                const fileData = {
                    ...file,
                    fileName: file.file_name || file.fileName,
                    url: file.view_url || file.url,
                    type: formatFileType(file.file_type || file.type)
                };
                setViewFile(fileData);
            } finally {
                setIsFetchingFile(false);
            }
        } else if (action === 'delete') {
            setFileIdToDelete(id);
        }
    };

    const confirmDelete = () => {
        if (fileIdToDelete) {
            onDelete(fileIdToDelete);
            setFileIdToDelete(null);
        }
    };

    const handleScroll = useCallback(() => {
        if (!scrollContainerRef.current || !onLoadMore || !hasMore || isFetchingMore || isLoading) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 20) {
            onLoadMore();
        }
    }, [onLoadMore, hasMore, isFetchingMore, isLoading]);

    useEffect(() => {
        const currentRef = scrollContainerRef.current;
        if (currentRef) {
            currentRef.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (currentRef) {
                currentRef.removeEventListener('scroll', handleScroll);
            }
        };
    }, [handleScroll, onLoadMore, hasMore, isFetchingMore, isLoading]); // Added dependencies for handleScroll

    if (isLoading) {
        return (
            <div className="files-table-container">
                <div style={{ padding: '40px', display: 'flex', justifyContent: 'center' }}>
                    <Loader />
                </div>
            </div>
        );
    }

    return (
        <div className="files-table-container">
            <div className="table-header table-row">
                <div className="name"><strong>File Name</strong></div>
                <div className="type"><strong>Type</strong></div>
                <div className="actions"><strong>Actions</strong></div>
            </div>

            <div
                className="table-body"
                ref={scrollContainerRef}
                style={{ maxHeight: '600px', overflowY: 'auto' }}
            >
                {files.length > 0 ? (
                    files.map((file) => (
                        <div key={file.id} className="table-row">
                            <div className="name">{file.file_name || file.fileName || '-'}</div>
                            <div className="type">{formatFileType(file.file_type || file.type)}</div>
                            <div className="actions">
                                <EyeIcon
                                    width="20"
                                    height="20"
                                    style={{ cursor: 'pointer', marginRight: '16px', color: '#238698' }}
                                    onClick={() => handleAction('view', file)}
                                />
                                <TrashIcon
                                    width="20"
                                    height="20"
                                    style={{ cursor: 'pointer', color: '#FF4D4F' }}
                                    onClick={() => handleAction('delete', file)}
                                />
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="table-row" style={{ justifyContent: 'center', color: '#666' }}>
                        No files found.
                    </div>
                )}
                {isFetchingMore && (
                    <div style={{ padding: '16px', display: 'flex', justifyContent: 'center' }}>
                        <Loader />
                    </div>
                )}
            </div>

            <ViewFileModal
                isOpen={!!viewFile}
                onClose={() => setViewFile(null)}
                file={viewFile}
            />

            <ConfirmModal
                isOpen={!!fileIdToDelete}
                onClose={() => setFileIdToDelete(null)}
                onConfirmClick={confirmDelete}
                title="Delete File"
                subtitle="Are you sure you want to delete this file? This action cannot be undone."
                confirmBtnText="Delete"
                cancelBtnText="Cancel"
            />
            {isFetchingFile && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <Loader />
                </div>
            )}
        </div>
    );
};

export default FilesTable;
